/**
 * upgrade-git-guard.ts — the git preconditions of `ss upgrade`.
 *
 * `ss upgrade` rewrites csproj files, configuration, generated code and docker
 * files in one pass. Without a clean starting point the user cannot tell the
 * upgrade's changes from their own, and a `git checkout .` to undo the upgrade
 * would also throw away their work. So, before the first write:
 *
 * - the working tree of the project must be clean (`--allow-dirty` opts out);
 *   the upgrade's own run artefacts (backups, report) never count as dirt;
 * - no merge / rebase / cherry-pick / revert may be in progress (no opt-out:
 *   the index is half-resolved, an upgrade on top of it is unrecoverable);
 * - `main`/`master`, `release/*` and `hotfix/*` are PROTECTED: the upgrade asks
 *   for an interactive confirmation on a terminal, and refuses without one
 *   (`--allow-protected-branch` is the explicit, scriptable consent);
 * - on `develop` it only SUGGESTS a feature branch — it never creates one
 *   (creating a branch is the user's decision, not the CLI's).
 *
 * Outside a git repository nothing can be checked: a warning is returned, the
 * upgrade backup (`upgrade-backup.ts`) is then the only safety net.
 */
import { execFileSync } from 'child_process';
import { existsSync } from 'fs';
import { isAbsolute, join } from 'path';

export type BranchType = 'feature' | 'release' | 'hotfix' | 'main' | 'develop' | 'other';

/**
 * MIRROR of `templates/skills/gitflow/cli/lib/branch.ts` → `detectBranchType`.
 * gitflow ships as a deployed skill (it imports nothing from `src/`), the CLI
 * cannot import it either — `upgrade-git-guard.test.ts` locks the two bodies
 * together, so a drift fails the suite instead of diverging silently.
 */
export function detectBranchType(branchName: string): BranchType {
  if (branchName === 'main' || branchName === 'master') return 'main';
  if (branchName === 'develop' || branchName === 'development') return 'develop';
  if (branchName.startsWith('feature/')) return 'feature';
  if (branchName.startsWith('release/')) return 'release';
  if (branchName.startsWith('hotfix/')) return 'hotfix';
  return 'other';
}

/** Branch types an upgrade must not land on without an explicit consent. */
export const PROTECTED_BRANCH_TYPES: readonly BranchType[] = ['main', 'release', 'hotfix'];

/** Paths (relative to the project root, forward slashes) the dirty check ignores: the upgrade's own artefacts. */
export const UPGRADE_ARTIFACT_PREFIXES: readonly string[] = [
  '.smartstack/upgrade-backups/',
  '.smartstack/upgrade-report.json',
];

export type InProgressOp = 'merge' | 'rebase' | 'cherry-pick' | 'revert';

export interface GitSafetyOptions {
  allowDirty?: boolean;
  allowProtected?: boolean;
}

export interface GitSafetyReport {
  /** False when `dir` is not inside a git work tree — nothing else was checked. */
  isRepo: boolean;
  /** Current branch, null on a detached HEAD or outside a repository. */
  branch: string | null;
  branchType: BranchType | null;
  /** Modified / untracked paths of the project (relative to the project root), upgrade artefacts excluded. */
  dirty: string[];
  inProgressOp: InProgressOp | null;
  /** The branch is main/master, release/* or hotfix/*. */
  protectedBranch: boolean;
  /** The protected branch still needs a consent: an interactive confirmation, or a refusal without a terminal. */
  needsConfirmation: boolean;
  /** Reasons the upgrade must NOT start. Empty = go (subject to `needsConfirmation`). */
  blockers: string[];
  /** Non-blocking findings (not a repository, detached HEAD). */
  warnings: string[];
  /** Advice that never blocks (develop → start a feature branch). */
  suggestions: string[];
  /** `git rev-parse HEAD`, null before the first commit or outside a repository. */
  head: string | null;
}

function git(dir: string, args: string[], raw = false): string | null {
  try {
    const out = execFileSync('git', args, { cwd: dir, encoding: 'utf-8', stdio: ['ignore', 'pipe', 'ignore'] });
    // Porcelain output starts with a significant space (` M file`) — never trim it.
    return raw ? out : out.trim();
  } catch {
    return null;
  }
}

/** Parse `git status --porcelain=v1 -z` (paths relative to the repository root). */
export function parsePorcelainZ(output: string): string[] {
  const paths: string[] = [];
  const records = output.split('\0');
  for (let i = 0; i < records.length; i++) {
    const record = records[i];
    if (record.length < 4) continue;
    const status = record.slice(0, 2);
    paths.push(record.slice(3));
    // A rename/copy carries its ORIGINAL path in the next NUL-separated field.
    if (status[0] === 'R' || status[0] === 'C') i++;
  }
  return paths;
}

function isArtifact(relToProject: string): boolean {
  return UPGRADE_ARTIFACT_PREFIXES.some((p) => relToProject === p || relToProject.startsWith(p));
}

/**
 * Inspect the git state of `dir` (the project root). Pure observation: runs
 * read-only git commands, never writes, never prompts. The caller decides what
 * to do with `needsConfirmation` (see {@link resolveProtectedBranch}).
 */
export function checkGitSafety(dir: string, opts: GitSafetyOptions = {}): GitSafetyReport {
  const report: GitSafetyReport = {
    isRepo: false,
    branch: null,
    branchType: null,
    dirty: [],
    inProgressOp: null,
    protectedBranch: false,
    needsConfirmation: false,
    blockers: [],
    warnings: [],
    suggestions: [],
    head: null,
  };

  if (git(dir, ['rev-parse', '--is-inside-work-tree']) !== 'true') {
    report.warnings.push(
      'Not a git repository — the upgrade cannot check for uncommitted work. ' +
        'Its backup (.smartstack/upgrade-backups/) is the only way back (ss upgrade --restore latest).',
    );
    return report;
  }
  report.isRepo = true;
  report.head = git(dir, ['rev-parse', '--verify', '-q', 'HEAD']) || null;

  // ── Operation in progress (no opt-out) ──────────────────────────────────
  const gitPath = (name: string): string | null => {
    const p = git(dir, ['rev-parse', '--git-path', name]);
    if (!p) return null;
    return isAbsolute(p) ? p : join(dir, p);
  };
  const markers: Array<[string, InProgressOp]> = [
    ['MERGE_HEAD', 'merge'],
    ['rebase-merge', 'rebase'],
    ['rebase-apply', 'rebase'],
    ['CHERRY_PICK_HEAD', 'cherry-pick'],
    ['REVERT_HEAD', 'revert'],
  ];
  for (const [marker, op] of markers) {
    const p = gitPath(marker);
    if (p && existsSync(p)) {
      report.inProgressOp = op;
      break;
    }
  }
  if (report.inProgressOp) {
    report.blockers.push(
      `A git ${report.inProgressOp} is in progress — finish or abort it (git ${report.inProgressOp} --continue / --abort) before upgrading.`,
    );
  }

  // ── Branch ──────────────────────────────────────────────────────────────
  const branch = git(dir, ['symbolic-ref', '--short', '-q', 'HEAD']);
  if (branch) {
    report.branch = branch;
    report.branchType = detectBranchType(branch);
  } else {
    report.branchType = 'other';
    report.warnings.push('Detached HEAD — the upgrade commits nothing, but start a branch before you commit its changes.');
  }
  report.protectedBranch = report.branchType !== null && PROTECTED_BRANCH_TYPES.includes(report.branchType);
  if (report.protectedBranch && !opts.allowProtected) report.needsConfirmation = true;
  if (report.branchType === 'develop') {
    report.suggestions.push(
      `You are on ${branch}: upgrade on a feature branch so the change goes through a reviewed PR ` +
        '(e.g. /gitflow start feature smartstack-upgrade). The upgrade never creates a branch itself.',
    );
  }

  // ── Working tree (scoped to the project directory) ──────────────────────
  const prefix = git(dir, ['rev-parse', '--show-prefix']) ?? '';
  const status = git(dir, ['status', '--porcelain=v1', '-z', '--untracked-files=all', '--', '.'], true);
  if (status === null) {
    report.warnings.push('Could not read git status — uncommitted work was NOT checked.');
  } else {
    for (const repoRel of parsePorcelainZ(status)) {
      const rel = prefix && repoRel.startsWith(prefix) ? repoRel.slice(prefix.length) : repoRel;
      if (!isArtifact(rel)) report.dirty.push(rel);
    }
  }
  if (report.dirty.length > 0 && !opts.allowDirty) {
    const sample = report.dirty.slice(0, 8).join(', ') + (report.dirty.length > 8 ? ', …' : '');
    report.blockers.push(
      `The working tree has ${report.dirty.length} uncommitted change(s) (${sample}). ` +
        'Commit or stash them first so the upgrade diff stays readable and undoable — or pass --allow-dirty.',
    );
  }

  return report;
}

export type ConfirmFn = (question: string) => Promise<boolean>;

/**
 * Settle a protected branch: consent given by flag → go; a terminal → ask
 * (default No); no terminal → refuse. Returns the blocker to add, or null.
 */
export async function resolveProtectedBranch(
  report: GitSafetyReport,
  io: { isTTY: boolean; confirm?: ConfirmFn },
): Promise<string | null> {
  if (!report.needsConfirmation) return null;
  const where = report.branch ?? 'this branch';
  if (!io.isTTY || !io.confirm) {
    return (
      `Refusing to upgrade on the protected branch ${where} without a terminal to confirm. ` +
      'Upgrade on a feature branch, or pass --allow-protected-branch if this is really intended.'
    );
  }
  const yes = await io.confirm(
    `You are on the protected branch ${where}. Upgrade here anyway (the change will not go through a feature PR)?`,
  );
  return yes ? null : `Upgrade cancelled on the protected branch ${where}.`;
}
